import { IconButton, Stack, SvgIcon, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { getStyles } from "./styles";
import { useNavigate } from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../../locales/tokens";
import { paths } from "../../../../../../paths";
import { useDispatch } from "react-redux";
import { IProduct } from "../../../../../../store/product/types/IProduct";
import { setSelectedProduct } from "../../../../../../store/product/repository/slice";

interface IProps {
    product: IProduct;
    onChangeVisibility: (id: number, isHidden: boolean) => void;
    onDelete: (id: number, mode?: string) => void;
};

export const ProductsTableItem: FC<IProps> = ({ product, onChangeVisibility, onDelete }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const styles = useMemo(() => getStyles(), []);
    const isActive = !product.deletedAt;

    const handleEdit = useCallback(() => {
        dispatch(setSelectedProduct(product));
        navigate(paths.dashboard.shop.edit.replace(':id', String(product.id)));
    }, []);

    const handleVisibility = useCallback(() => onChangeVisibility(product.id, !product.is_hidden), [product]);

    const handleDelete = useCallback(() => {
        if (product.deletedAt) {
            onDelete(product.id, 'hard');
        } else {
            onDelete(product.id);
        }
    }, [product.id]); 

    return (
        <TableRow hover key={product.id}>
            <TableCell align="left" width="50%" sx={styles.cell}>
                <Stack direction={'row'} alignItems={'center'}>
                    <img
                        src={product.files?.[0]?.url || "/images/user/uploadImage.png"}
                        alt="AppPageIcon"
                        width={45}
                        height={45}
                        className="image"
                    />
                    <Typography ml={1} sx={styles.title} variant="body2">{product.name}</Typography>
                </Stack>
            </TableCell>
            <TableCell align="center" width="15%" sx={styles.cell}>
                <Typography variant="body2">{product.quantity + t(tokens.shop.amount)}</Typography>
            </TableCell>
            <TableCell align="center" width="15%" sx={styles.cell}>
                <Typography variant="body2">{product.price + t(tokens.shop.points)}</Typography>
            </TableCell>
            <TableCell align="right" width="25%" sx={styles.cell}>
                <Stack direction='row' alignItems='center' spacing={1} justifyContent='flex-end'>
                    {isActive &&
                        <IconButton size="small" onClick={handleVisibility}>
                            {product.is_hidden
                                ? <Tooltip title={t(tokens.documents.notPublish)}>
                                    <SvgIcon style={styles.svgIcon}><VisibilityOffOutlinedIcon /></SvgIcon>
                                </Tooltip>
                                : <Tooltip title={t(tokens.documents.publish)} >
                                    <SvgIcon style={styles.svgIcon}><VisibilityOutlinedIcon /></SvgIcon>
                                </Tooltip>
                            }
                        </IconButton>
                    }
                    <IconButton size="small" onClick={handleEdit}>
                        <Tooltip title={isActive ? t(tokens.common.edit) : t(tokens.appPage.show)}>
                            <SvgIcon style={styles.svgIcon}><EditOutlinedIcon /></SvgIcon>
                        </Tooltip>
                    </IconButton>
                    {isActive ?
                        <IconButton size="small" onClick={handleDelete}>
                            <Tooltip title={t(tokens.common.delete)}>
                                <SvgIcon style={styles.svgIcon}><DeleteForeverIcon /></SvgIcon>
                            </Tooltip>
                        </IconButton>
                        : <IconButton size="small" onClick={handleDelete}>
                            <Tooltip title={t(tokens.common.deleteHard)}>
                                <SvgIcon style={styles.svgIcon}><DeleteForeverIcon /></SvgIcon>
                            </Tooltip>
                        </IconButton>
                    }
                </Stack>
            </TableCell>
        </TableRow>
    )
};